import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
  useContext
} from 'react';
import {Avatar, IconButton, Typography} from '@customink/pigment-react';
import ArrowUpIcon from '../../assets/icons/arrow_up.svg';
import ArrowDownIcon from '../../assets/icons/arrow_down.svg';
import {MobileContext} from '../../containers/MobileProvider';

type AccordionMenuItemProps = {
  title: string;
  subtitle?: string;
  children: JSX.Element | JSX.Element[];
  menuExpanded?: boolean;
  onToggle?: () => void;
  hideTitle?: boolean;
  userEmail?: string;
};

const AccordionMenuItem: React.FC<AccordionMenuItemProps> = ({
  title,
  subtitle,
  children,
  menuExpanded,
  onToggle,
  hideTitle,
  userEmail
}: AccordionMenuItemProps): ReactElement => {
  const [expanded, setExpanded] = useState(
    menuExpanded !== undefined ? menuExpanded : false
  );
  const mobile = useContext(MobileContext);

  function toggleMenu() {
    if (onToggle !== undefined) {
      onToggle();
    } else {
      setExpanded(!expanded);
    }
  }

  const onArrowClick = () => {
    toggleMenu();
  };

  const onEnterKeyDown = useCallback(
    (
      e:
        | React.KeyboardEvent<HTMLButtonElement>
        | React.KeyboardEvent<HTMLDivElement>
    ) => {
      if (e.key === 'Enter') {
        toggleMenu();
      }
    },
    [onToggle, expanded]
  );

  useEffect(() => {
    if (menuExpanded !== undefined) {
      setExpanded(menuExpanded);
    }
  }, [menuExpanded]);

  interface MenuButtonProps {
    icon?: 'default' | 'avatar';
  }

  const MenuButton = ({icon}: MenuButtonProps): ReactElement => {
    const defaultIcon = expanded ? (
      <img src={ArrowUpIcon} alt="Collapse" />
    ) : (
      <img src={ArrowDownIcon} alt="Expand" />
    );
    const avatarIcon = (
      <Avatar data={userEmail} size="large" className="userAvatar" />
    );
    return (
      <IconButton
        size="small"
        className="accordionMenuItem-action"
        aria-label={expanded ? 'Collapse' : 'Expand'}
        onClick={onArrowClick}
        onKeyDown={onEnterKeyDown}
        tabIndex={0}>
        {icon === 'avatar' ? avatarIcon : defaultIcon}
      </IconButton>
    );
  };

  MenuButton.defaultProps = {
    height: 'default'
  };

  const MenuItemHeader = () => {
    const divAttributes = {
      className: 'accordionMenuItem-header',
      onClick: onArrowClick,
      onKeyDown: onEnterKeyDown,
      tabIndex: 0,
      role: 'button'
    };

    return subtitle ? (
      <>
        <div {...divAttributes}>
          {!hideTitle && (
            <>
              <span className="accordionMenuItem-title">
                <Typography variant="buttonSm">{title}</Typography>
              </span>
              <div className="accordionMenuItem-subtitle">
                <Typography variant="buttonSm">{subtitle}</Typography>
                <MenuButton />
              </div>
            </>
          )}
        </div>
        {hideTitle && <MenuButton icon={userEmail ? 'avatar' : 'default'} />}
      </>
    ) : (
      <div {...divAttributes}>
        {!hideTitle && (
          <span className="accordionMenuItem-title">
            <Typography variant="buttonLg">{title}</Typography>
          </span>
        )}
        <MenuButton />
      </div>
    );
  };

  return (
    <div className="accordionMenuItem">
      <MenuItemHeader />
      <div
        className={`accordionMenuItem-content${mobile ? ' mobile' : ''}${
          hideTitle ? ' hideTitle' : ''
        }`}
        hidden={!expanded}>
        {children}
      </div>
    </div>
  );
};

AccordionMenuItem.defaultProps = {
  menuExpanded: undefined,
  subtitle: undefined,
  onToggle: undefined,
  hideTitle: false,
  userEmail: null
};

export default AccordionMenuItem;
